import { defineStore, acceptHMRUpdate } from 'pinia'
import { computed, type ComputedRef } from 'vue'
import { useCorrectionStore } from '@/stores/CorrectionStore'
import { type Timesheet as TTimesheet } from '@/api/client/models/Timesheet'
import { type TimesheetsTotals as TTimesheetsTotals } from '@/api/client/models/TimesheetsTotals'
import { TimesheetsService } from '@/api/client'
import type { MonthNumbers } from 'luxon'
import { DateTime } from 'luxon'
import handleRequest from '@/helpers/handleRequest'
import { storeToRefs } from 'pinia'
import { useConfigStore } from '@/stores/ConfigStore'
import { useEmployeeStore } from './EmployeeStore'

type TState = {
  _timesheets: TTimesheet[]
  totals: TTimesheetsTotals
  lastUpdate: DateTime | null
  showWeeklyWorkingHours: boolean
}
const initialState: TState = {
  _timesheets: [],
  totals: {
    flextime_seconds: 0,
    max_vacation_days: 0,
    planned_vacation_days: 0,
    used_vacation_days: 0,
    max_vacation_seconds: 0,
    planned_vacation_seconds: 0,
    used_vacation_seconds: 0,
  },
  lastUpdate: null,
  showWeeklyWorkingHours: false,
}

const useTimesheetStore = defineStore('timesheetStore', {
  state: () => {
    return { ...initialState }
  },
  getters: {
    targetSeconds: (state) => {
      return (month: number): number =>
        state._timesheets.find((timesheet) => timesheet.month === month)
          ?.target_seconds ?? 0
    },
    workSeconds: (state) => {
      return (month: number): number =>
        state._timesheets.find((timesheet) => timesheet.month === month)
          ?.work_seconds ?? 0
    },
    workSecondsOfWeek: (state) => {
      return (month: number, weekStart: string): number => {
        const timesheetOfMonth = state._timesheets.find(
          (timesheet) => timesheet.month === month,
        )
        if (!timesheetOfMonth) return 0
        const week = timesheetOfMonth.calendar_weeks[weekStart]
        return week.work_seconds ?? 0
      }
    },
    liveAnnualTimeAccount: (
      state,
    ): { hours: number; minutes: number; formatted: string } => {
      const correctionStore = useCorrectionStore()
      const annualOvertimeSeconds = state.totals.flextime_seconds ?? 0
      let overtimeSeconds = annualOvertimeSeconds
      if (
        correctionStore.lastWorkday &&
        correctionStore.lastWorkday?.target_seconds
      ) {
        const todaysTimeAccount =
          correctionStore.lastEventWorkSeconds +
          correctionStore.lastWorkday.work_seconds -
          correctionStore.lastWorkday?.target_seconds
        const todaysOvertimeSeconds =
          todaysTimeAccount > 0 ? todaysTimeAccount : 0
        overtimeSeconds += todaysOvertimeSeconds
      }
      const round = overtimeSeconds > 0 ? Math.floor : Math.ceil
      const overtimeYearHours = Math.abs(round(overtimeSeconds / 3600))
      const overtimeMinutes = Math.abs(round((overtimeSeconds % 3600) / 60))
      const isOvertime = overtimeSeconds > 0
      const prefix = overtimeSeconds === 0 ? '' : isOvertime ? '+' : '-'
      const hoursString = overtimeYearHours.toFixed()
      const minutesString =
        overtimeMinutes < 10
          ? '0' + overtimeMinutes.toString()
          : overtimeMinutes.toString()
      return {
        hours: overtimeYearHours,
        minutes: overtimeMinutes,
        formatted: `${prefix}${hoursString}:${minutesString}`,
      }
    },
    liveWorkSecondsOfCurrentMonth(): number {
      const correctionStore = useCorrectionStore()
      return (
        useTimesheetStore().workSeconds(DateTime.now().month) +
        correctionStore.lastEventWorkSeconds
      )
    },
    liveWorkSecondsOfCurrentWeek(): number {
      const correctionStore = useCorrectionStore()
      const now = DateTime.now()

      return (
        useTimesheetStore().workSecondsOfWeek(
          now.month,
          now.startOf('week').toFormat('yyyy-MM-dd'),
        ) + correctionStore.lastEventWorkSeconds
      )
    },
    timesheets: (state) => state._timesheets,
    months(): Array<
      ComputedRef<{
        monthNumber: MonthNumbers
        monthName: string
        progress: number
        hoursView: string
        targetHours: string
      }>
    > {
      const { language } = storeToRefs(useConfigStore())
      function calcProgress(workSeconds: number, targetSeconds: number) {
        if (workSeconds <= 0 || targetSeconds <= 0) return 0
        const progress = (workSeconds / targetSeconds) * 100
        return Math.min(progress, 100)
      }
      function calcHoursView(workSeconds: number) {
        const hours = Math.floor(workSeconds / 3600)
        const minutes = Math.floor(workSeconds / 60)
        const formattedMinutes = Math.floor(minutes % 60)
        const paddedHours = String(hours).padStart(2, '0')
        const paddedMinutes = String(formattedMinutes).padStart(2, '0')
        const view = `${paddedHours}:${paddedMinutes}`
        return view
      }
      function calcTargetHours(targetSeconds: number) {
        const onlyFullHours = (targetSeconds ?? 0) % 3600 === 0
        const hours = Math.floor(targetSeconds / 3600)
        if (onlyFullHours) {
          return `${hours}`
        }
        const minutes = ((targetSeconds % 3600) / 60)
          .toFixed(0)
          .padStart(2, '0')

        return `${hours}:${minutes}`
      }

      return this.timesheets.map((sheet) => {
        const { month, year } = sheet
        const targetSeconds = this.targetSeconds(month)
        const date = DateTime.fromObject({ year: year, month: month })

        if (date.month === DateTime.now().month) {
          return computed(() => {
            const workSeconds = this.liveWorkSecondsOfCurrentMonth
            return {
              monthNumber: date.month,
              monthName: date.toLocaleString(
                { month: 'long', year: 'numeric' },
                { locale: language.value ?? 'de' },
              ),
              progress: calcProgress(workSeconds, targetSeconds),
              hoursView: calcHoursView(workSeconds),
              targetHours: calcTargetHours(targetSeconds),
            }
          })
        }
        return computed(() => {
          const workSeconds = this.workSeconds(date.month)
          return {
            monthNumber: date.month,
            monthName: date.toLocaleString(
              { month: 'long', year: 'numeric' },
              { locale: language.value ?? 'de' },
            ),
            progress: calcProgress(workSeconds, targetSeconds),
            hoursView: calcHoursView(workSeconds),
            targetHours: calcTargetHours(targetSeconds),
          }
        })
      })
    },
    monthNames: (state) => {
      return state._timesheets.map((sheet) => {
        const { month, year } = sheet
        const date = DateTime.fromObject({ year: year, month: month })
        return {
          title: date.toLocaleString({ month: 'long', year: 'numeric' }),
          value: date.year + '-' + String(date.month).padStart(2, '0'),
        }
      })
    },
    noData: (state) => {
      return state.lastUpdate === null
    },
  },
  actions: {
    async sync() {
      const employee = useEmployeeStore().employee
      if (employee?.can_show_times) {
        await this.syncTimesheets()
      }
      if (employee?.can_request_absences || employee?.can_show_times) {
        await this.syncTotals()
      }
      this.$state.lastUpdate = DateTime.now()
    },

    async syncTimesheets() {
      const timesheets = (
        await handleRequest(
          TimesheetsService.getTimesheets(
            DateTime.now()
              .minus({ years: 1 })
              .plus({ months: 1 })
              .startOf('month')
              .toISO(),
            DateTime.now().endOf('month').toISO(),
          ),
        )
      )?.data
      this._timesheets = timesheets || []
      console.log(this.timesheets)
    },
    async syncTotals() {
      const totals = (await handleRequest(TimesheetsService.getTotals()))?.data
      this.totals = totals || initialState.totals
    },
    hydrate(state: string) {
      this.$state = JSON.parse(state, (key, value) => {
        if (key === 'lastUpdate') {
          return DateTime.fromISO(value)
        } else {
          return value
        }
      })
    },
  },
})

if (import.meta.hot) {
  import.meta.hot.accept(acceptHMRUpdate(useTimesheetStore, import.meta.hot))
}

export { useTimesheetStore }
